/**
 * Academic Admin → Exam Timetables: the set-up behind the Exams tab pupils
 * and parents see.
 *
 * The office decides three things and nothing else — the dates are
 * Cambridge's, extracted into src/content/examTimetables/ and not editable
 * here:
 *
 *   1. which classes sit the series, and at which level (Form 5 → IGCSE,
 *      AS → AS, A2 → A Level);
 *   2. for each subject those pupils take, the syllabus and the papers — the
 *      Extended papers by default, the school's standing entry;
 *   3. when it is ready, Publish. Pupils and parents cannot read a draft
 *      (firestore.rules), so a half-finished set-up cannot reach a phone.
 *
 * Papers are chosen per SUBJECT, not per pupil, deliberately: Core, Extended
 * and Alternative to Practical papers share a date and session, so a pupil
 * entered differently still has exactly the same timetable.
 *
 * Gate `canManageExamTimetables` (admin, system admin, academic admin),
 * mirrored by canManageExamTimetables() in the rules.
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, onSnapshot, query, serverTimestamp, where } from 'firebase/firestore';
import {
  AlertTriangle, CalendarClock, CheckCircle2, Eye, RotateCcw, Save, Send, Undo2, Users,
} from 'lucide-react';
import { db, setDoc } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { SCHOOL, termStartDate } from '@/lib/school.config';
import {
  Page, PageHeader, PanelHeader, SoftCard, StatChip, StatStrip, btnGhost, btnPrimary, glassFieldClass,
} from '@/components/ui/design';
import {
  CLASS_LEVELS,
  DEFAULT_SESSIONS,
  componentsOf,
  defaultComponents,
  draftSubjects,
  normaliseSubjectId,
  seriesGaps,
  sittingsFor,
  type CambridgeTimetable,
  type ExamLevel,
  type ExamSeries,
  type ExamSubjectEntry,
  type PupilExamContext,
  type PupilExamEntries,
} from '@/lib/examTimetable';
import ExamTimetableView from './ExamTimetableView';
import { loadTimetable, useMinuteClock, type SeriesSitting } from './useExamTimetable';

/** The series this screen sets up. One today; the school's own Term 1 and 2
 *  exams will be a second kind of series when their timetables exist. */
const CURRENT = {
  id: 'cambridge-2026-11',
  title: 'Cambridge November 2026',
  timetableId: 'cambridge-2026-11-zone3',
};

/** Which classes are exam classes by default, by the class's form. */
function defaultLevel(form: string): ExamLevel | null {
  const f = String(form || '').trim().toUpperCase();
  if (f === '5') return 'IGCSE';
  if (f === 'AS') return 'AS';
  if (f === 'A2') return 'A Level';
  return null;
}

function isoDay(d: Date | null): string {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/**
 * Moved out of Academic Admin into the Exams group on 2026-09-18, screen
 * unchanged. `#/examTimetables` still addresses it — the old nav entry, the
 * dashboard card and anybody's bookmark all speak that id.
 */
export default function ExamTimetablesAdmin() {
  return <Page wide><Body /></Page>;
}

function Body() {
  const { userData, permissions } = useAuth();
  const { classes, students, subjects } = useData();
  const now = useMinuteClock();

  const [timetable, setTimetable] = useState<CambridgeTimetable | null>(null);
  const [saved, setSaved] = useState<ExamSeries | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState<ExamSeries | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [previewId, setPreviewId] = useState('');

  useEffect(() => { void loadTimetable(CURRENT.timetableId).then(setTimetable); }, []);

  /* Statements of entry imported for this series (seed/import-statement-of-entry.ts).
     Where a pupil has one it decides their papers exactly; the subject rows
     below are only the fallback for pupils without. */
  const [entries, setEntries] = useState<Map<string, PupilExamEntries>>(new Map());
  useEffect(() => {
    void getDocs(query(collection(db, 'reportExamEntries'), where('seriesId', '==', CURRENT.id)))
      .then((snap) => setEntries(new Map(snap.docs.map((d) => {
        const e = d.data() as PupilExamEntries;
        return [e.studentId, e] as const;
      }))))
      .catch(() => setEntries(new Map()));
  }, []);

  useEffect(() => onSnapshot(
    doc(collection(db, 'reportExamSeries'), CURRENT.id),
    (snap) => {
      setSaved(snap.exists() ? ({ ...(snap.data() as ExamSeries), id: snap.id }) : null);
      setLoaded(true);
    },
    (e) => { console.error(e); setLoaded(true); },
  ), []);

  const subjectName = useMemo(() => {
    const m = new Map(subjects.map((s) => [normaliseSubjectId(s.id), s.name?.trim() || s.id]));
    return (id: string) => m.get(normaliseSubjectId(id)) ?? id;
  }, [subjects]);

  const classById = useMemo(() => new Map(classes.map((c) => [c.id, c])), [classes]);

  /** Every current pupil, as the series sees them: class DISPLAY name and subjects. */
  const pupils = useMemo(() => students
    .filter((s) => s.status !== 'left')
    .map((s) => ({
      id: s.id,
      name: s.fullName,
      ctx: { className: classById.get(s.currentClass)?.name ?? '', subjectIds: s.subjects ?? [] } as PupilExamContext,
    })), [students, classById]);

  /* The working copy. A saved series is edited in place; with none, a draft
     is built from the exam classes and the subjects their pupils take. */
  useEffect(() => {
    if (!loaded || !timetable || draft) return;
    if (saved) { setDraft(saved); return; }
    const examClasses = classes
      .map((c) => ({ className: c.name, level: defaultLevel(c.form) }))
      .filter((c): c is { className: string; level: ExamLevel } => !!c.level)
      .sort((a, b) => a.className.localeCompare(b.className));
    setDraft({
      id: CURRENT.id,
      kind: 'cambridge',
      title: CURRENT.title,
      timetableId: CURRENT.timetableId,
      published: false,
      countdownFrom: isoDay(termStartDate(SCHOOL.academicYearDefault, 'Term3')) || `${new Date().getFullYear()}-09-01`,
      sessions: DEFAULT_SESSIONS,
      classes: examClasses,
      subjects: draftSubjects(examClasses, timetable, pupils.map((p) => p.ctx)),
    });
  }, [loaded, timetable, saved, draft, classes, pupils]);

  if (!permissions.canManageExamTimetables) {
    return <SoftCard><p className="text-sm text-[var(--sp-body)]">You do not have access to exam timetables.</p></SoftCard>;
  }
  if (!timetable || !loaded || !draft) {
    return <p className="py-8 text-sm text-[var(--sp-muted)]">Loading…</p>;
  }

  /* Paper times come from the live copy: they are edited on Exams → Timetable. */
  const live: ExamSeries = { ...draft, paperTimes: saved?.paperTimes };
  const dirty = stableJson(stripMeta(draft)) !== stableJson(saved ? stripMeta(saved) : null);
  const inSeries = pupils.filter((p) => draft.classes.some((c) => c.className.trim().toLowerCase() === p.ctx.className.trim().toLowerCase()));
  const gaps = seriesGaps(draft, timetable, inSeries.map((p) => p.ctx));
  const allSittings = inSeries.map((p) => sittingsFor(live, timetable, p.ctx, entries.get(p.id)?.components));
  const withStatement = inSeries.filter((p) => entries.has(p.id)).length;
  const firstDate = [...allSittings.flat()].sort((a, b) => a.date.localeCompare(b.date))[0]?.date;
  const withNone = inSeries.filter((_, i) => allSittings[i].length === 0).length;

  const preview = inSeries.find((p) => p.id === previewId) ?? inSeries[0];
  const previewSittings: SeriesSitting[] = preview
    ? sittingsFor(live, timetable, preview.ctx, entries.get(preview.id)?.components).map((s) => ({ ...s, seriesId: draft.id, countdownFrom: draft.countdownFrom }))
    : [];

  const set = (patch: Partial<ExamSeries>) => setDraft({ ...draft, ...patch });
  const setEntry = (i: number, patch: Partial<ExamSubjectEntry>) =>
    set({ subjects: draft.subjects.map((e, j) => (j === i ? { ...e, ...patch } : e)) });

  async function save(published: boolean) {
    if (!draft) return;
    setBusy(true);
    setMessage(null);
    try {
      const body: ExamSeries = {
        ...stripMeta(draft),
        /* Paper times are edited from the cards on Exams → Timetable, not
           here, so the LIVE copy is kept: this screen's draft was read when it
           opened and would otherwise put back times changed since. */
        paperTimes: saved?.paperTimes ?? {},
        published,
        updatedAt: serverTimestamp(),
        updatedBy: userData?.uid ?? '',
        updatedByName: userData?.displayName ?? '',
      };
      await setDoc(doc(collection(db, 'reportExamSeries'), draft.id), body as unknown as Record<string, unknown>);
      setDraft({ ...draft, published });
      setMessage({
        tone: 'ok',
        text: published
          ? 'Published. Pupils and parents in these classes now see their exams.'
          : saved?.published ? 'Unpublished and saved. Pupils and parents no longer see it.' : 'Saved as a draft.',
      });
    } catch (e) {
      console.error(e);
      setMessage({ tone: 'error', text: 'Could not save. Check your connection and try again.' });
    } finally {
      setBusy(false);
    }
  }

  /** Add rows for subjects pupils take that have none, keeping every row already set. */
  function addMissingSubjects() {
    if (!draft || !timetable) return;
    const have = new Set(draft.subjects.map((e) => `${e.level}|${normaliseSubjectId(e.subjectId)}`));
    const extra = draftSubjects(draft.classes, timetable, inSeries.map((p) => p.ctx))
      .filter((e) => !have.has(`${e.level}|${normaliseSubjectId(e.subjectId)}`));
    set({ subjects: [...draft.subjects, ...extra] });
  }

  const pupilCount = (e: ExamSubjectEntry) => inSeries.filter((p) =>
    draft.classes.find((c) => c.className.trim().toLowerCase() === p.ctx.className.trim().toLowerCase())?.level === e.level
    && p.ctx.subjectIds.some((s) => normaliseSubjectId(s) === normaliseSubjectId(e.subjectId))).length;

  const syllabusOptions = (level: ExamLevel) => {
    const family: ExamLevel[] = level === 'AS' || level === 'A Level' ? ['AS', 'A Level'] : [level];
    const m = new Map<string, string>();
    for (const c of timetable.components) if (family.includes(c.level) && !m.has(c.syllabus)) m.set(c.syllabus, c.subject);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  };

  return (
    <>
      <PageHeader
        icon={CalendarClock}
        eyebrow="Exams"
        title="Candidates and entries"
        subtitle={`${draft.title} · Zone 3 dates from Cambridge's final timetable`}
        actions={(
          <div className="flex flex-wrap gap-2">
            {dirty && saved && (
              <button type="button" className={btnGhost} onClick={() => setDraft(saved)} disabled={busy}>
                <Undo2 size={15} /> Discard changes
              </button>
            )}
            <button type="button" className={btnGhost} onClick={() => void save(draft.published)} disabled={busy || (!dirty && !!saved)}>
              <Save size={15} /> Save
            </button>
            {draft.published ? (
              <button type="button" className={btnGhost} onClick={() => void save(false)} disabled={busy}>
                Unpublish
              </button>
            ) : (
              <button type="button" className={btnPrimary} onClick={() => void save(true)} disabled={busy}>
                <Send size={15} /> Publish
              </button>
            )}
          </div>
        )}
      />

      <StatStrip>
        <StatChip tone={draft.published ? 'emerald' : 'amber'} value={draft.published ? 'Live' : 'Draft'} label={draft.published ? 'Pupils and parents see it' : 'Not visible to pupils yet'} icon={draft.published ? CheckCircle2 : Eye} />
        <StatChip tone="navy" value={inSeries.length} label={`pupils in ${draft.classes.length} classes`} icon={Users} />
        <StatChip tone={withStatement === inSeries.length && inSeries.length ? 'emerald' : 'amber'} value={`${withStatement}/${inSeries.length}`} label="statements of entry imported" icon={CalendarClock} />
        <StatChip tone={gaps.length || withNone ? 'red' : 'emerald'} value={gaps.length} label={firstDate ? `to check · first paper ${shortDate(firstDate)}` : 'to check'} icon={AlertTriangle} />
      </StatStrip>

      {message && (
        <p className={`mb-4 rounded-lg border px-4 py-2 text-sm ${message.tone === 'ok'
          ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
          : 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200'}`}
        >
          {message.text}
        </p>
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-5">
          <SoftCard pad="">
            <PanelHeader icon={CalendarClock} title="Series" subtitle="When the countdown starts and the school's session times" />
            <div className="grid gap-4 p-4 sm:grid-cols-3">
              <label className="text-xs text-[var(--sp-muted)]">
                Countdown starts
                <input type="date" className={glassFieldClass('mt-1 w-full')} value={draft.countdownFrom} onChange={(e) => set({ countdownFrom: e.target.value })} />
              </label>
              {(['AM', 'PM'] as const).map((k) => (
                <div key={k} className="text-xs text-[var(--sp-muted)]">
                  {k === 'AM' ? 'Morning session' : 'Afternoon session'}
                  <div className="mt-1 flex items-center gap-1">
                    <input type="time" className={glassFieldClass('w-full')} value={draft.sessions[k].start}
                      onChange={(e) => set({ sessions: { ...draft.sessions, [k]: { ...draft.sessions[k], start: e.target.value } } })} />
                    <span>–</span>
                    <input type="time" className={glassFieldClass('w-full')} value={draft.sessions[k].end}
                      onChange={(e) => set({ sessions: { ...draft.sessions, [k]: { ...draft.sessions[k], end: e.target.value } } })} />
                  </div>
                </div>
              ))}
            </div>
          </SoftCard>

          <SoftCard pad="">
            <PanelHeader icon={Users} title="Classes sitting this series" subtitle="A pupil sees exams only if their class is ticked" />
            <div className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3">
              {[...classes].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
                .filter((c) => defaultLevel(c.form) || draft.classes.some((x) => x.className === c.name))
                .map((c) => {
                  const entry = draft.classes.find((x) => x.className === c.name);
                  const count = pupils.filter((p) => p.ctx.className === c.name).length;
                  return (
                    <div key={c.id} className="flex items-center justify-between gap-2 rounded-lg border border-[var(--sp-line)] px-3 py-2">
                      <label className="flex min-w-0 items-center gap-2 text-sm text-[var(--sp-body)]">
                        <input
                          type="checkbox"
                          checked={!!entry}
                          onChange={(e) => set({
                            classes: e.target.checked
                              ? [...draft.classes, { className: c.name, level: defaultLevel(c.form) ?? 'IGCSE' }]
                              : draft.classes.filter((x) => x.className !== c.name),
                          })}
                        />
                        <span className="truncate font-semibold text-ink dark:text-slate-100">{c.name}</span>
                        <span className="text-xs text-[var(--sp-muted)]">{count}</span>
                      </label>
                      {entry && (
                        <select
                          className={glassFieldClass('w-28 py-1 text-xs')}
                          value={entry.level}
                          onChange={(e) => set({ classes: draft.classes.map((x) => (x.className === c.name ? { ...x, level: e.target.value as ExamLevel } : x)) })}
                        >
                          {CLASS_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                      )}
                    </div>
                  );
                })}
            </div>
          </SoftCard>

          <SoftCard pad="p-4">
            <p className="text-sm text-[var(--sp-body)]">
              {withStatement > 0 ? (
                <>
                  <b className="text-ink dark:text-slate-100">{withStatement} of {inSeries.length} pupils</b> have their Cambridge statement of entry imported, so their
                  timetables show exactly the papers they are entered for. The subject rows below apply only to pupils without one
                  {withStatement < inSeries.length ? ` (${inSeries.length - withStatement})` : ' (none at the moment)'}.
                </>
              ) : (
                <>No statements of entry imported yet: every pupil's papers come from the subject rows below. Import the CIE Direct statement (seed/import-statement-of-entry.ts) for exact per-pupil papers.</>
              )}
            </p>
          </SoftCard>

          {gaps.length > 0 && (
            <SoftCard pad="">
              <PanelHeader
                icon={AlertTriangle}
                tone="gold"
                title="To check"
                subtitle="Pupils who would be missing papers"
                right={gaps.some((g) => g.kind === 'noEntry')
                  ? <button type="button" className={btnGhost} onClick={addMissingSubjects}>Add missing subjects</button>
                  : undefined}
              />
              <ul className="divide-y divide-[var(--sp-line)] text-sm">
                {gaps.map((g) => (
                  <li key={`${g.level}|${g.subjectId}|${g.kind}`} className="flex items-center justify-between gap-3 px-4 py-2">
                    <span className="text-[var(--sp-body)]">
                      <span className="font-semibold text-ink dark:text-slate-100">{subjectName(g.subjectId)}</span> ({g.level}) —{' '}
                      {g.kind === 'noEntry' && 'no row below yet'}
                      {g.kind === 'noSyllabus' && 'marked as sitting no Cambridge exam'}
                      {g.kind === 'unknownSyllabus' && 'that syllabus is not in this timetable'}
                      {g.kind === 'noComponents' && 'no papers ticked'}
                    </span>
                    <span className="shrink-0 text-xs text-[var(--sp-muted)]">{g.pupils} pupils</span>
                  </li>
                ))}
              </ul>
            </SoftCard>
          )}

          {CLASS_LEVELS.filter((level) => draft.classes.some((c) => c.level === level)).map((level) => (
            <SoftCard key={level} pad="">
              <PanelHeader icon={CalendarClock} title={`${level} subjects`} subtitle="The syllabus and the papers sat. Extended papers are ticked by default." />
              <ul className="divide-y divide-[var(--sp-line)]">
                {draft.subjects.map((e, i) => ({ e, i })).filter(({ e }) => e.level === level)
                  .sort((a, b) => subjectName(a.e.subjectId).localeCompare(subjectName(b.e.subjectId)))
                  .map(({ e, i }) => {
                    const rows = e.syllabus ? componentsOf(timetable, level, e.syllabus) : [];
                    return (
                      <li key={`${e.level}|${e.subjectId}`} className="px-4 py-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-ink dark:text-slate-100">
                            {subjectName(e.subjectId)}
                            <span className="ml-2 text-xs font-normal text-[var(--sp-muted)]">{pupilCount(e)} pupils</span>
                          </p>
                          <div className="flex items-center gap-2">
                            <select
                              className={glassFieldClass('w-64 py-1 text-xs')}
                              value={e.syllabus}
                              onChange={(ev) => setEntry(i, {
                                syllabus: ev.target.value,
                                components: ev.target.value ? defaultComponents(timetable, level, ev.target.value) : [],
                              })}
                            >
                              <option value="">No Cambridge exam this series</option>
                              {syllabusOptions(level).map(([code, name]) => <option key={code} value={code}>{code} {name}</option>)}
                            </select>
                            {e.syllabus && (
                              <button
                                type="button"
                                title="Tick the default papers again"
                                className="rounded-md border border-[var(--sp-line)] p-1.5 text-[var(--sp-muted)] hover:text-[var(--sp-body)]"
                                onClick={() => setEntry(i, { components: defaultComponents(timetable, level, e.syllabus) })}
                              >
                                <RotateCcw size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                        {rows.length > 0 && (
                          <div className="mt-2 grid gap-1 sm:grid-cols-2">
                            {rows.map((r) => (
                              <label key={r.code} className="flex items-start gap-2 text-xs text-[var(--sp-body)]">
                                <input
                                  type="checkbox"
                                  className="mt-0.5"
                                  checked={e.components.includes(r.component)}
                                  onChange={(ev) => setEntry(i, {
                                    components: ev.target.checked
                                      ? [...e.components, r.component].sort()
                                      : e.components.filter((x) => x !== r.component),
                                  })}
                                />
                                <span>
                                  <span className="font-semibold tabular-nums">{r.code}</span>
                                  {r.part ? ` ${r.part}` : ''}
                                  {r.level !== level ? ` (${r.level})` : ''}
                                  <span className="text-[var(--sp-muted)]"> · {shortDate(r.date)} {r.session} · {r.duration}</span>
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </li>
                    );
                  })}
              </ul>
            </SoftCard>
          ))}
        </div>

        <div className="min-w-0">
          <SoftCard pad="" className="xl:sticky xl:top-4">
            <PanelHeader icon={Eye} title="What a pupil sees" subtitle="Including unsaved changes" />
            <div className="space-y-3 p-4">
              <select className={glassFieldClass('w-full')} value={preview?.id ?? ''} onChange={(e) => setPreviewId(e.target.value)}>
                {[...inSeries].sort((a, b) => a.ctx.className.localeCompare(b.ctx.className) || a.name.localeCompare(b.name)).map((p) => (
                  <option key={p.id} value={p.id}>{p.ctx.className} · {p.name}</option>
                ))}
              </select>
              {preview && (
                <ExamTimetableView
                  sittings={previewSittings}
                  loading={false}
                  error={null}
                  now={now}
                  who={preview.name.split(' ')[0]}
                  title={draft.title}
                />
              )}
              {withNone > 0 && (
                <p className="text-xs text-rose-600 dark:text-rose-300">
                  {withNone} pupil{withNone === 1 ? '' : 's'} in these classes would see no exams at all.
                </p>
              )}
            </div>
          </SoftCard>
        </div>
      </div>
    </>
  );
}

/** The fields that are the set-up, without who saved it when — and without
 *  the paper times, which are edited from Exams → Timetable. */
function stripMeta(s: ExamSeries): ExamSeries {
  const { updatedAt: _a, updatedBy: _b, updatedByName: _c, paperTimes: _d, ...rest } = s;
  return rest as ExamSeries;
}

/** JSON with keys sorted: a document read back from Firestore does not keep
 *  the key order it was written in, and "unsaved changes" must not light up
 *  for a series nobody has touched. */
function stableJson(v: unknown): string {
  return JSON.stringify(v, (_k, x) => (x && typeof x === 'object' && !Array.isArray(x)
    ? Object.fromEntries(Object.entries(x as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)))
    : x));
}
